import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Filter } from '../component/filter'
import { TableAG } from './table-ag'
import { ColumnPropsProvider, getColumns } from './table-columns'
import { useSelectColumn } from './grid-selection'
import { useGetQueryValues, useSetQueryValues } from './hooks'
import { QueryItems } from './component/table-query-items'
import { useTableToolbar } from './table-toolbar'
import { useAlertException } from '../alert-exception/context'
import { LiveIndicator } from './component/live-indicator'
import { useFirstLoadSettled } from '../empty-state/empty-state'
import { runInBackground } from '../api.service'

// The id of the grid's first visible column, or null. getColumns() answers
// null before the grid has columns and undefined once it is destroyed
// (production error #8: "e.api.getColumns().filter" on the Organizations
// page, Safari), so neither is assumed.
export const firstVisibleColId = (api) => {
	const columns = api && typeof api.getColumns === 'function' ? api.getColumns() : null
	if (!Array.isArray(columns)) return null
	const column = columns.find((c) => c && (typeof c.isVisible === 'function' ? c.isVisible() : c.visible))
	if (!column) return null
	return column.colId ?? (typeof column.getColId === 'function' ? column.getColId() : null) ?? null
}

// The grid's default sort: the page's sortCol, else its first visible column.
// A grid with no columns yet gets no sort now and is sorted once, when its
// columns arrive; a destroyed grid gets nothing.
export const applyDefaultSort = (api, { sortCol, sortDesc } = {}) => {
	if (!api || api.isDestroyed?.()) return
	const sort = sortDesc ? 'desc' : 'asc'
	const colId = sortCol || firstVisibleColId(api)
	if (colId) {
		api.applyColumnState({ state: [{ colId, sort }] })
		return
	}
	if (typeof api.addEventListener !== 'function') return
	const handleColumns = () => {
		api.removeEventListener('newColumnsLoaded', handleColumns)
		if (api.isDestroyed?.()) return
		const firstColId = firstVisibleColId(api)
		if (firstColId) api.applyColumnState({ state: [{ colId: firstColId, sort }] })
	}
	api.addEventListener('newColumnsLoaded', handleColumns)
}

// `queryKey` namespaces this grid's filter/sort/scroll query-string keys (see
// table-builder/hooks.js). Set it on EVERY grid on a page that shows more than
// one, or they share -- and overwrite -- each other's state. Omitting it keeps
// the historical unprefixed keys.
//
// `pinFirstColumn`: on a narrow grid (a phone) the first column stays pinned
// on the left while the rest scroll sideways (TableAG, NARROW_GRID_PX).
//
// `emptyState` ({ Icon, title, message, action, secondary } -- see
// shared/empty-state) is what the grid shows when there is nothing yet;
// `noMatchState` optionally overrides the "no matches for your search" copy.
//
// `getRowId` (AG Grid's: params => a row's id, as a string): the grid keeps
// the rows it has when the data changes (a reload, a minute clock) instead of
// drawing every row again. Used whenever the page gives one; a polled grid
// without one gets the table's own (the row's id, else its content). AG Grid
// 33 reads it once, when the grid is made (an initial-only option): pass a
// stable function, module-level or useCallback, never an inline one, and
// don't switch it on later.
//
// selection: the page's select mode (grid-selection.js), as on
// TableAgInfinite: its checkbox column while selecting, a row click ticks,
// and a change of the quick filter clears it. "All matching" is every row
// the page handed the grid; a page whose server takes ids sends
// selectedIdsOf(selection, rows).
export const TableAgClient = forwardRef(({ children, apiList, queryKey, sortCol, sortDesc, onReady, onRowClicked, onDestoryed, pollInterval, emptyState, noMatchState, getRowClass, rowHeight, pinFirstColumn, getRowId, selection }, ref) => {
	const tableRef = useRef()
	const [pollReady, setPollReady] = useState(false)
	const [liveHealth, setLiveHealth] = useState('live')
	const [liveUpdatedAt, setLiveUpdatedAt] = useState(null)

	const alertException = useAlertException()
	const toolbar = useTableToolbar()

	const [filter, updateFilter] = useState('')

	// Skeleton rows until the first fetch settles -- not an empty grid that
	// briefly claims there is nothing. Later reloads and polls never bring the
	// loading overlay back.
	const [readyDone, updateReadyDone] = useState(false)
	const firstLoadSettled = useFirstLoadSettled(apiList)
	const loading = !firstLoadSettled && (!readyDone || !!apiList?.loading || !!apiList?.chunking)

	const { columnChildren, clearSelection } = useSelectColumn(children, selection)
	let columns = useMemo(() => getColumns(columnChildren), [columnChildren])

	const getQueryValues = useGetQueryValues(queryKey)
	const setQueryValues = useSetQueryValues(queryKey)

	const data = useMemo(() => {
		if (!apiList) {
			return []
		}

		return apiList.items
	}, [apiList])

	useEffect(() => {
		if (tableRef.current.api) {
			tableRef.current.api.setGridOption('quickFilterText', filter)
		}
	}, [filter])

	const handleReady = async (agGrid) => {
		
		if (onReady) {
			try {
				await onReady(agGrid)
			} catch (ex) {
				alertException.updateException(ex)
			}
		}

		updateReadyDone(true)

		// The page's onReady awaited a request: the grid may have been destroyed
		// meanwhile (the page left), and a destroyed grid's API answers undefined.
		const api = agGrid?.api
		if (!api || api.isDestroyed?.()) return

		let queryValues = getQueryValues()

		if (queryValues.sortCol) {
			const columnState = {
				state: [{ colId: queryValues.sortCol, sort: sortDesc ? 'desc' : 'asc' }]
			}

			api.applyColumnState(columnState)
			setPollReady(true)
			return
		}

		applyDefaultSort(api, { sortCol, sortDesc })

		setPollReady(true)
	}

	useEffect(() => {
		if (!pollInterval || !pollReady) return

		let interval = null

		const poll = async () => {
			if (!onReady) return
			try {
				await runInBackground(() => onReady())
				setLiveHealth('live')
				setLiveUpdatedAt(new Date())
			} catch {
				setLiveHealth('reconnecting')
			}
		}

		const startPolling = () => {
			if (interval) return
			interval = setInterval(poll, pollInterval)
		}

		const stopPolling = () => {
			if (interval) {
				clearInterval(interval)
				interval = null
			}
		}

		const handleVisibility = () => {
			if (document.visibilityState === 'visible') {
				poll() // refresh immediately on tab focus
				startPolling()
			} else {
				stopPolling()
			}
		}

		document.addEventListener('visibilitychange', handleVisibility)

		// Start polling if tab is currently visible
		if (document.visibilityState === 'visible') {
			startPolling()
		}

		return () => {
			stopPolling()
			document.removeEventListener('visibilitychange', handleVisibility)
		}
	}, [pollInterval, onReady, pollReady])

	// Prefer a real id; aggregate report rows have none, so fall back to a content
	// key. This keeps row identity stable across polls (unchanged rows stay put,
	// new sessions stream in) instead of every row colliding on a single "undefined".
	const pollRowId = useCallback((params) => String(params.data.id ?? JSON.stringify(params.data)), [])

	const handleDataUpdated = () => {
		// Load-bearing for the zero state too: hydrating `filter` from the URL here
		// is what makes `searchActive` (and so "No matches") true for a stale
		// ?search= that hides every row. An empty list skips it, and correctly
		// reads "nothing yet".
		if (data.length) {
			const queryValues = getQueryValues()

			if (queryValues.search) {
				updateFilter(queryValues.search)
			}

			if (queryValues.index !== undefined) {
				tableRef.current?.api?.ensureIndexVisible(queryValues.index, 'top')
			}
		}
	}

	const handleScrollVertical = (index) => {
		setQueryValues(undefined, index, undefined, undefined)
	}

	const handleFilterChange = (filter) => {
		updateFilter(filter)
		setQueryValues(filter, 0, undefined, undefined)
		clearSelection()
	}

	const handleClearState = () => {
		setQueryValues(undefined, 0, null, null)
		tableRef.current?.api?.resetColumnState()
		tableRef.current?.api?.paginationGoToPage(0)
	}

	const handleClearSearch = () => {
		updateFilter('')
		setQueryValues('', 0, undefined, undefined)
		clearSelection()
		tableRef.current?.api?.paginationGoToPage(0)
	}

	const handleSort = (params) => {
		// AG Grid's sortChanged event may come without its columns.
		let column = (params?.columns || []).findLast(() => true)

		if (column) {
			if (column.sort) {
				setQueryValues(undefined, undefined, column.colId, column.sort)
			} else {
				setQueryValues(undefined, undefined, null, null)
			}
			// Scroll back to the top -- only when there is a row to scroll to
			// (AG Grid logs error #88 for ensureIndexVisible on an empty grid).
			const api = tableRef.current?.api
			if (api && api.getDisplayedRowCount() > 0) api.ensureIndexVisible(0, 'top')
		}
	}

	return (
		<div className='d-flex flex-column h-100'>
			<div>
				<QueryItems queryKey={queryKey} columns={columns} rows={data} onClearSort={handleClearState} onClearSearch={handleClearSearch} />
			</div>
			<div className={`d-flex align-items-center gap-2${toolbar.start || toolbar.end ? ' tb-toolbar' : ''}`} data-testid='table-toolbar'>
				{toolbar.start ? <div className='tb-toolbar-start'>{toolbar.start}</div> : null}
				<div className={toolbar.start || toolbar.end ? 'tb-toolbar-filter' : 'flex-fill'}>
					<Filter value={filter} onChange={handleFilterChange} />
				</div>
				{pollInterval ? <LiveIndicator health={liveHealth} lastUpdatedAt={liveUpdatedAt} /> : null}
				{toolbar.end ? <div className='tb-toolbar-end'>{toolbar.end}</div> : null}
			</div>
			<div className='flex-fill'>
				{/* the cells of the page's selector, menu and link columns read its latest props (table-columns.js) */}
				<ColumnPropsProvider columns={columnChildren}>
					<TableAG
						ref={tableRef}
						funcRef={ref}
						columns={columns}
						data={data}
						loading={loading}
						emptyState={emptyState}
						noMatchState={noMatchState}
						searchActive={!!filter}
						onClearSearch={handleClearSearch}
						getRowId={getRowId || (pollInterval ? pollRowId : undefined)}
						sortFirst
						sortCol={sortCol}
						sortDesc={sortDesc}
						onReady={handleReady}
						onRefresh={handleReady}
						onRowClicked={onRowClicked}
						getRowClass={getRowClass}
						rowHeight={rowHeight}
						pinFirstColumn={pinFirstColumn}
						selection={selection}
						onScrollVertical={handleScrollVertical}
						onDataUpdated={handleDataUpdated}
						onSortChanged={handleSort}
						onDestoryed={onDestoryed}
					/>
				</ColumnPropsProvider>
			</div>
		</div>
	)
})
